import React from "react";
import { useForm, Controller } from "react-hook-form";
import { useAuthState, useContributorsActions } from "@/store";
import { USER_EXISTS_STATUS } from "@/utils/userManagementErrors";
import {
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import {
  FormContainer,
  AuthTitleBlock,
  TitleText,
  FormWrapper,
  ButtonWrapper,
  SubmitButton,
} from "../AuthForm/AuthSteps/Auth.elements";

const AddNewUser = ({ onClose }) => {
  const { currentUser } = useAuthState();
  const { role } = currentUser || {};

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    watch,
    setError,
    clearErrors,
  } = useForm({ mode: "onSubmit", reValidateMode: "onSubmit" });

  const { createContributor } = useContributorsActions();
  const canManageAdmin = role === "admin";
  const canManageManager = role === "manager";

  const availableRoles = canManageAdmin
      ? ["admin", "manager", "contributor"]
      : canManageManager
          ? ["manager", "contributor"]
          : [];

  const name = (watch("name") || "").trim();
  const email = (watch("email") || "").trim();
  const roleValue = (watch("role") || "").trim();

  const allFilled = !!name && !!email && !!roleValue;

  const onSubmit = async (payload) => {
    const result = await createContributor(payload);
    if (result.meta.requestStatus === "fulfilled") {
      onClose();
      return;
    }
    // Duplicate email: pin the reason to the field so it stays visible after the toast fades.
    if (result.payload?.status === USER_EXISTS_STATUS) {
      setError("email", {
        type: "server",
        message: result.payload?.message || "A user with this email already exists.",
      });
    }
  };

  return (
      <FormContainer>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormWrapper>
            <TextField
                fullWidth
                label="Name"
                placeholder="Enter the name"
                {...register("name", { required: "Name is required" })}
                error={!!errors.name}
                helperText={errors.name?.message || " "}
            />

            <TextField
                fullWidth
                label="Email"
                placeholder="Enter your email"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[^@ ]+@[^@ ]+\.[^@ .]{2,}$/,
                    message: "Invalid email address",
                  },
                  // Errors are only re-validated on submit, but the Add button is disabled while
                  // any error is set - clear this field's error as soon as the person edits it.
                  onChange: () => clearErrors("email"),
                })}
                error={!!errors.email}
                helperText={errors.email?.message || " "}
            />

            <FormControl fullWidth error={!!errors.role}>
              <InputLabel id="role-select-label">Role</InputLabel>
              <Controller
                  name="role"
                  control={control}
                  defaultValue=""
                  rules={{ required: "Role is required" }}
                  render={({ field }) => (
                      <Select
                          {...field}
                          labelId="role-select-label"
                          label="Role"
                          disabled={availableRoles.length === 0}
                      >
                        {availableRoles.map((roleOption) => (
                            <MenuItem key={roleOption} value={roleOption}>
                              {roleOption}
                            </MenuItem>
                        ))}
                        {availableRoles.length === 0 && (
                            <MenuItem value="" disabled>
                              No roles available
                            </MenuItem>
                        )}
                      </Select>
                  )}
              />
              <FormHelperText>{errors.role?.message || " "}</FormHelperText>
            </FormControl>
          </FormWrapper>

          <ButtonWrapper>
            <SubmitButton
                buttonState="auth"
                type="submit"
                disabled={!allFilled || Object.keys(errors).length > 0}
            >
              Add
            </SubmitButton>
          </ButtonWrapper>
        </form>
      </FormContainer>
  );
};

export default AddNewUser;
